import { Autocomplete, Checkbox, Divider, FormControl, FormControlLabel, FormHelperText, Paper, TextField } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Controller, UseFormReturn, useWatch } from "react-hook-form";

import DynamicIcon from "./dynamic_icon";

/**
 * DefaultFormFillableInput
 */

type Props<T, F extends {}> = {
  name: keyof F;
  label?: string;
  options: T[];
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  fillableBoxLabel?: string;
  fillableMode?: boolean;
  newButtonLabel?: string;
  reactHookForm: UseFormReturn<F>;
  getOptionLabel: (opt: T) => string;
  getOptionKey: (opt: T) => string | number;
  getOptionDisabled?: (opt: T) => boolean;
  onNew?: () => void;
};

export const DefaultFormFillableInput = <T, F extends {},>({
  name,
  label,
  options,
  required,
  readOnly,
  disabled,
  fillableBoxLabel,
  newButtonLabel,
  reactHookForm,
  getOptionLabel,
  getOptionKey,
  getOptionDisabled,
  onNew,
}: Props<T, F>) => {
  const [fillable, setFillable] = useState(false);
  const fieldValue = useWatch({ control: reactHookForm.control, name: name as any });

  const CustomPaper = useMemo(() => (({ onClick, children, ...props }: any) => (
    <Paper {...props}>
      <div>
        {children}
        <Divider />
        <div>
          <div className="py-2 px-4 cursor-pointer rounded-[4px] font-semibold" onMouseDown={onClick}>
            <button type="button" className="text-sm text-primary-green flex flex-row items-center gap-1">
              <DynamicIcon nameIcon="Add" size={20} className="text-primary-green" />
              <span className="mt-[0.05rem]">{newButtonLabel ?? "Novo"}</span>
            </button>
          </div>
        </div>
      </div>
    </Paper>
  )), [
    newButtonLabel,
  ]);

  useEffect(() => {
    if (fieldValue !== null && fieldValue !== undefined) {
      setFillable(typeof fieldValue === 'string');
    }
  }, [fieldValue]);

  return (
    <Controller
      name={name as any}
      control={reactHookForm.control}
      render={({ field, formState }) => {
        let value: T | string | null = null;
        let helperText: string | null = null;

        if (field.value) {
          if (typeof field.value === 'object') {
            value = field.value as T;
          } else if (typeof field.value === 'string') {
            value = field.value as string;
          }
        } else {
          value = null;
        }

        if (name in formState.errors) {
          const error = formState.errors[name] as any;
          if (typeof error.message === 'string') {
            helperText = error.message;
          }
        }

        return (
          <FormControl>
            <div style={{ height: 45 }}>
              {
                !fillable ?
                  (
                    <Autocomplete
                      size="small"
                      getOptionLabel={(opt) => getOptionLabel(opt)}
                      getOptionKey={(opt) => getOptionKey(opt)}
                      options={options}
                      sx={{ minHeight: 45 }}
                      readOnly={readOnly}
                      disabled={disabled}
                      getOptionDisabled={getOptionDisabled}
                      slots={{
                        paper: onNew ? CustomPaper : undefined,
                      }}
                      slotProps={{
                        paper: {
                          onClick: onNew,
                        },
                      }}
                      renderOption={(props, opt) => (
                        <li {...props}>
                          <span style={{ whiteSpace: 'pre-line' }}>{getOptionLabel(opt)}</span>
                        </li>
                      )}
                      value={typeof value === 'object' ? value as T : null}
                      onChange={(_, newValue) => field.onChange(newValue)}
                      renderInput={(params) => <TextField {...params}
                        size="small"
                        label={label}
                        error={helperText !== null && helperText !== undefined}
                        helperText={helperText}
                        required={required} />}
                    />
                  ) : (
                    <TextField
                      size="small"
                      fullWidth={true}
                      error={helperText !== null && helperText !== undefined}
                      helperText={helperText}
                      value={typeof value === 'string' ? value : null}
                      onChange={e => field.onChange(e.target.value)}
                    />
                  )
              }
            </div>
            <FormControlLabel
              label={fillableBoxLabel ?? 'Preencher o Fornecedor'}
              sx={{ paddingLeft: '5px' }}
              control={
                <Checkbox
                  checked={fillable ?? false}
                  onChange={e => {
                    reactHookForm.resetField(name as any);
                    setFillable(prev => !prev);
                  }}
                />
              }
            />
            <FormHelperText id={`${name as string}-component-error-text`} error>
              {helperText}
            </FormHelperText>
          </FormControl>
        );
      }}
    />
  );
}
